'use client';

import { useState } from 'react';
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { 
  Eye, EyeOff, Mail, Lock, ArrowRight, ArrowLeft, 
  ShieldCheck, Loader2, KeyRound, CheckCircle2 
} from 'lucide-react';

function LoginForm() {
  const { login } = useAuth();
  
  // Controle de Telas (Máquina de Estados)
  const [view, setView] = useState<'login' | 'forgot' | 'code' | 'reset'>('login');
  
  // Estados dos Inputs
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  
  // Toggles de Visibilidade de Senha
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Estados de Feedback
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';

  // ==========================================
  // FLUXO 1: LOGIN
  // ==========================================
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Credenciais inválidas.');
      }

      login(data.access_token, data.user);
    } catch (err: any) {
      setError(err.message === 'Failed to fetch' ? 'Erro de conexão com o servidor.' : err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // FLUXO 2: SOLICITAR CÓDIGO (ESQUECI A SENHA)
  // ==========================================
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return setError('Por favor, informe seu e-mail primeiro.');
    
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Erro ao enviar código.');

      setSuccess('Código enviado! Verifique sua caixa de entrada.');
      setTimeout(() => {
        setSuccess('');
        setView('code'); // Avança para a tela de digitar código
      }, 2000);

    } catch (err: any) {
      setError(err.message === 'Failed to fetch' ? 'Erro de conexão com o servidor.' : err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // FLUXO 3: VALIDAR CÓDIGO
  // ==========================================
  const handleValidateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code) return setError('Por favor, digite o código recebido.');
    
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/auth/validate-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Código inválido ou expirado.');

      setView('reset'); // Avança para a tela de nova senha
    } catch (err: any) {
      setError(err.message === 'Failed to fetch' ? 'Erro de conexão com o servidor.' : err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // FLUXO 4: REDEFINIR SENHA
  // ==========================================
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) return setError('A nova senha deve ter no mínimo 6 caracteres.');
    
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, newPassword }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Erro ao redefinir senha.');

      setSuccess('Senha alterada com sucesso! Faça login.');
      setPassword(''); // Limpa a senha antiga do input
      setTimeout(() => {
        setSuccess('');
        setView('login'); // Volta para o login
      }, 3000);

    } catch (err: any) {
      setError(err.message === 'Failed to fetch' ? 'Erro de conexão com o servidor.' : err.message);
    } finally {
      setLoading(false);
    }
  };

  // Função auxiliar para voltar ao Login e limpar erros
  const backToLogin = () => {
    setError('');
    setSuccess('');
    setCode('');
    setNewPassword('');
    setView('login');
  };

  // Aqui está a correção principal visual: o formulário ocupa 100% DO PAI, e o pai que trava o tamanho
  return (
    <div className="w-full bg-white/90 backdrop-blur-xl p-8 sm:p-10 rounded-[2rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)] border border-white transition-all duration-500 relative overflow-hidden">
      
      {/* Header com Logo */}
      <div className="text-center mb-8 relative z-10">
        <div className="flex justify-center mb-4">
          <img 
            src="/logo-nexus.png" 
            alt="NexusSafe" 
            style={{ height: '48px', width: 'auto' }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          <div style={{ display: 'none' }} className="items-center gap-2 justify-center">
            <ShieldCheck size={36} className="text-blue-600" />
            <span className="text-2xl font-black text-gray-900 tracking-tight">NEXUS<span className="font-light">SAFE</span></span>
          </div>
        </div>
        
        {view === 'login' && <p className="text-sm font-medium text-gray-500">Acesse seu ambiente seguro</p>}
        {view === 'forgot' && <p className="text-sm font-medium text-gray-500">Recuperação de conta</p>}
        {view === 'code' && <p className="text-sm font-medium text-gray-500">Validação de segurança</p>}
        {view === 'reset' && <p className="text-sm font-medium text-gray-500">Crie sua nova senha</p>}
      </div>

      {/* Alertas de Erro ou Sucesso */}
      {error && (
        <div className="mb-6 p-3 px-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm font-bold rounded-r-xl animate-in slide-in-from-top-2">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 p-3 px-4 bg-green-50 border-l-4 border-green-500 text-green-700 text-sm font-bold rounded-r-xl flex items-center gap-2 animate-in slide-in-from-top-2">
          <CheckCircle2 size={18} /> {success}
        </div>
      )}

      {/* ======================= TELA DE LOGIN ======================= */}
      {view === 'login' && (
        <form onSubmit={handleLogin} className="space-y-5 relative z-10 animate-in fade-in zoom-in-95 duration-300">
          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">E-mail corporativo</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                <Mail size={18} />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all font-semibold text-gray-800"
                placeholder="seu@email.com"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Senha</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                <Lock size={18} />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-11 pr-12 py-3.5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all font-semibold text-gray-800"
                placeholder="••••••"
                required
              />
              <button 
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-blue-600 transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            
            <div className="flex justify-end mt-2">
              <button 
                type="button" 
                onClick={() => setView('forgot')}
                className="text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors"
              >
                Esqueceu sua senha?
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-black uppercase tracking-widest text-sm hover:bg-blue-700 hover:-translate-y-1 transition-all shadow-lg shadow-blue-600/30 disabled:opacity-50 disabled:hover:translate-y-0 flex items-center justify-center gap-2 mt-2"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : 'Entrar no Sistema'}
          </button>
        </form>
      )}

      {/* ======================= TELA: ESQUECI A SENHA ======================= */}
      {view === 'forgot' && (
        <form onSubmit={handleForgotPassword} className="space-y-5 relative z-10 animate-in slide-in-from-right-8 duration-300">
          <p className="text-sm text-gray-600 font-medium text-center mb-6">
            Digite seu e-mail cadastrado. Enviaremos um código de segurança para você criar uma nova senha.
          </p>

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">E-mail cadastrado</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                <Mail size={18} />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all font-semibold text-gray-800"
                placeholder="seu@email.com"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-black uppercase tracking-widest text-sm hover:bg-blue-700 hover:-translate-y-1 transition-all shadow-lg shadow-blue-600/30 disabled:opacity-50 disabled:hover:translate-y-0 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : 'Enviar Código'}
          </button>

          <button type="button" onClick={backToLogin} className="w-full flex items-center justify-center gap-2 text-sm font-bold text-gray-500 hover:text-gray-800 transition-colors mt-4">
            <ArrowLeft size={16} /> Voltar para o Login
          </button>
        </form>
      )}

      {/* ======================= TELA: DIGITAR CÓDIGO ======================= */}
      {view === 'code' && (
        <form onSubmit={handleValidateCode} className="space-y-5 relative z-10 animate-in slide-in-from-right-8 duration-300">
          <p className="text-sm text-gray-600 font-medium text-center mb-6">
            Enviamos um código para <strong className="text-gray-900">{email}</strong>. Digite-o abaixo.
          </p>

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Código de Segurança</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                <KeyRound size={18} />
              </div>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all font-black text-gray-800 tracking-widest text-center"
                placeholder="000000"
                maxLength={6}
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-black uppercase tracking-widest text-sm hover:bg-blue-700 hover:-translate-y-1 transition-all shadow-lg shadow-blue-600/30 disabled:opacity-50 disabled:hover:translate-y-0 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : 'Validar Código'}
          </button>

          <button type="button" onClick={backToLogin} className="w-full flex items-center justify-center gap-2 text-sm font-bold text-gray-500 hover:text-gray-800 transition-colors mt-4">
            Cancelar e Voltar
          </button>
        </form>
      )}

      {/* ======================= TELA: NOVA SENHA ======================= */}
      {view === 'reset' && (
        <form onSubmit={handleResetPassword} className="space-y-5 relative z-10 animate-in slide-in-from-right-8 duration-300">
          <p className="text-sm text-gray-600 font-medium text-center mb-6">
            Código validado! Crie uma nova senha segura para sua conta.
          </p>

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Nova Senha</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                <Lock size={18} />
              </div>
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full pl-11 pr-12 py-3.5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all font-semibold text-gray-800"
                placeholder="Mínimo 6 caracteres"
                required
                minLength={6}
              />
              <button 
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-blue-600 transition-colors"
              >
                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-black uppercase tracking-widest text-sm hover:bg-blue-700 hover:-translate-y-1 transition-all shadow-lg shadow-blue-600/30 disabled:opacity-50 disabled:hover:translate-y-0 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : 'Salvar Nova Senha'}
          </button>
        </form>
      )}

      {/* Footer Segurança */}
      <div className="mt-8 text-center text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center justify-center gap-1.5 border-t border-gray-100 pt-6 relative z-10">
        <Lock size={12} className="text-green-500" />
        SST Criptografado de Ponta a Ponta
      </div>

    </div>
  );
}

// Wrapper principal
export default function Home() {
  return (
    <AuthProvider>
      <div className="flex flex-col items-center justify-center min-h-screen w-full bg-slate-50 relative overflow-hidden p-6 selection:bg-blue-200 selection:text-blue-900">
        {/* Padrão de fundo tecnológico */}
        <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/graphy.png")' }}></div>
        
        {/* CONTAINER TRAVADO: É aqui que garantimos a largura máxima no desktop */}
        <div className="relative z-10 w-full max-w-md mx-auto">
           <LoginForm />
        </div>
        
        {/* Efeito Visual de Fundo (Blur Circle) */}
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl pointer-events-none z-0"></div>
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-indigo-400/10 rounded-full blur-3xl pointer-events-none z-0"></div>
      </div>
    </AuthProvider>
  );
}